import { AxiosService } from '../../../../axios/axios.service';
import {
  tButtonsConfig,
  tFlowsConfig,
  tFollowUpConfig,
  tHandoffSchedule,
  tSmartLeadbotConfigPayload,
  tSmartLeadbotConfigResponse,
  tCalendarConfig,
} from './SmartLeadbotConfig.types';

export const smartLeadbotConfigService = {
  getConfig: async function (workspaceId: string): Promise<tSmartLeadbotConfigResponse> {
    try {
      const response = await AxiosService.get<tSmartLeadbotConfigResponse>(
        `/ai/workspaces/${workspaceId}/smartleadbot/config`,
        { isLegacy: false },
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  patchAiRespond: async function (
    workspaceId: string,
    aiRespondEnabled: boolean,
  ): Promise<void> {
    // RET-1069 V2: reverted to 2 args. The V1 review's symmetric reason
    // body is gone — under the post-success flow the modal fires AFTER
    // this PATCH, so reason fields are captured as PostHog events only.
    // If BE persistence becomes required later, a separate endpoint
    // `POST /smartleadbot/handoff-reasons` is the right shape.
    try {
      await AxiosService.patch<{ aiRespondEnabled: boolean }, unknown>(
        `/ai/workspaces/${workspaceId}/smartleadbot/config`,
        { aiRespondEnabled },
        { isLegacy: false },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  patchFunnelId: async function (
    workspaceId: string,
    funnelId: string,
  ): Promise<void> {
    try {
      await AxiosService.patch<{ funnelId: string }, unknown>(
        `/ai/workspaces/${workspaceId}/smartleadbot/config`,
        { funnelId },
        { isLegacy: false },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // AGI-16: persist the multi-funnel set. funnelIds is authoritative on chatbot-service
  // (via the AGI-15 bff-web contract) — it replaces the workspace's bot-active funnels.
  // Same PATCH endpoint as patchFunnelId; the body key is funnelIds.
  patchFunnelIds: async function (
    workspaceId: string,
    funnelIds: string[],
  ): Promise<void> {
    try {
      await AxiosService.patch<{ funnelIds: string[] }, unknown>(
        `/ai/workspaces/${workspaceId}/smartleadbot/config`,
        { funnelIds },
        { isLegacy: false },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // RET-1292: handoffSchedule rides the PATCH endpoint, never the bulk PUT —
  // the BFF's SmartLeadbotConfigRequest (PUT body) has no handoffSchedule field,
  // only patchSmartLeadbotConfigArgs does. Isolating it here mirrors patchFunnelId
  // / patchAiRespond and keeps the fragile bulk-PUT payload untouched.
  patchHandoffSchedule: async function (
    workspaceId: string,
    handoffSchedule: tHandoffSchedule,
  ): Promise<void> {
    try {
      await AxiosService.patch<{ handoffSchedule: tHandoffSchedule }, unknown>(
        `/ai/workspaces/${workspaceId}/smartleadbot/config`,
        { handoffSchedule },
        { isLegacy: false },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // AGI-105: the coexistence opt-in rides the PATCH endpoint. aiRespondEnabled is
  // optional so the same call covers both shapes:
  //   - opt-in / opt-out of coexistence alone: { coexistenceEnabled }
  //   - the ATOMIC exit (AGI-114 Opción 1 and the agent-uncheck):
  //     { coexistenceEnabled: false, aiRespondEnabled: false } in ONE request —
  //     chatbot-service's AGI-108 guard evaluates the incoming flag, so splitting
  //     it into two PATCHes would get the disable dropped against the stale
  //     stored coexistence on the first one.
  patchCoexistence: async function (
    workspaceId: string,
    body: { coexistenceEnabled: boolean; aiRespondEnabled?: boolean },
  ): Promise<void> {
    try {
      await AxiosService.patch<
        { coexistenceEnabled: boolean; aiRespondEnabled?: boolean },
        unknown
      >(`/ai/workspaces/${workspaceId}/smartleadbot/config`, body, {
        isLegacy: false,
      });
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // AGI-71: followUp rides the PATCH endpoint like handoffSchedule — the bulk PUT
  // body has no followUp field on the BFF, only patchSmartLeadbotConfigArgs does.
  patchFollowUp: async function (
    workspaceId: string,
    followUp: tFollowUpConfig,
  ): Promise<void> {
    try {
      await AxiosService.patch<{ followUp: tFollowUpConfig }, unknown>(
        `/ai/workspaces/${workspaceId}/smartleadbot/config`,
        { followUp },
        { isLegacy: false },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // AGI-TBD: calendar rides the PATCH endpoint like followUp/buttons — the bulk
  // PUT body has no calendar field on the BFF, only patchSmartLeadbotConfigArgs
  // does. The whole sub-doc travels on every save (the backend replaces it).
  patchCalendar: async function (
    workspaceId: string,
    calendar: tCalendarConfig,
  ): Promise<void> {
    try {
      await AxiosService.patch<{ calendar: tCalendarConfig }, unknown>(
        `/ai/workspaces/${workspaceId}/smartleadbot/config`,
        { calendar },
        { isLegacy: false },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // AGI-129: buttons rides the PATCH endpoint like followUp — the bulk PUT
  // body has no buttons field on the BFF, only patchSmartLeadbotConfigArgs does.
  patchButtons: async function (
    workspaceId: string,
    buttons: tButtonsConfig,
  ): Promise<void> {
    try {
      await AxiosService.patch<{ buttons: tButtonsConfig }, unknown>(
        `/ai/workspaces/${workspaceId}/smartleadbot/config`,
        { buttons },
        { isLegacy: false },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // AGI-141: flows rides the PATCH endpoint like followUp/handoffSchedule —
  // the bulk PUT body has no flows field on the BFF, only
  // patchSmartLeadbotConfigArgs does.
  patchFlows: async function (
    workspaceId: string,
    flows: tFlowsConfig,
  ): Promise<void> {
    try {
      await AxiosService.patch<{ flows: tFlowsConfig }, unknown>(
        `/ai/workspaces/${workspaceId}/smartleadbot/config`,
        { flows },
        { isLegacy: false },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  updateConfig: async function (
    workspaceId: string,
    payload: tSmartLeadbotConfigPayload,
  ): Promise<tSmartLeadbotConfigResponse> {
    try {
      const response = await AxiosService.put<
        tSmartLeadbotConfigPayload,
        tSmartLeadbotConfigResponse
      >(`ai/workspaces/${workspaceId}/smartleadbot/config`, payload, {
        isBFFService: true,
      });
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
